import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useLocation, useRoute, Link } from "wouter";
import { useSEO } from "@/hooks/useSEO";
import { getLocalizedRegionSEO } from "@/lib/seoUtils";
import { Store, Filter, SortAsc, CheckCircle, Loader2, Search, HelpCircle, PlusCircle, Plus, MapPin as MapPinIcon, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { supabase } from "@/lib/supabase";

import { ShopCard } from "@/components/ShopCard";
import { PriceLevel } from "@/components/PriceLevel";
import { ShopsSearchMapOsm } from "@/components/ShopsSearchMapOsm";
import { LoginModal } from "@/components/LoginModal";
import { UpgradeModal } from "@/components/UpgradeModal";
import { useAuth } from "@/lib/authContext";
import { getCurrencyForCountry, detectUserCountry } from "@/lib/countryDetection";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import type { Shop } from "@shared/schema";
import { getProvinceCenter, getCityCenter } from "@/lib/provinceCoordinates";
import { slugify } from "@/lib/slugify";
import { useShopInteractions } from "@/contexts/ShopInteractionsContext";
import { getAlternatePlaceName } from "@shared/placeMappings";
import { setUserCountry } from "@/lib/countryDetection";
import { EUROPE_COUNTRIES, normalizeCountryCode } from "@shared/europe-countries";
import { useRouteBuilder } from "@/contexts/RouteBuilderContext";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { CATEGORIES } from "@shared/schema";
import { CreditCard, Banknote, Clock, Car } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { getPreferredGeo } from "@/lib/geoPreferences";

import { cn } from "@/lib/utils";
import FeatureGuard from "@/components/FeatureGuard";
import OfflineCache from "@/lib/offline-cache";


type SortOption = "overall" | "vintmap" | "eigen" | "google" | "popular" | "distance";
const PRIORITY_COUNTRY_CODES = ["NL", "BE", "FR", "UK", "DK"];

export default function ShopsPage(props: any) {
  return <ShopsPageContent initialFilter={props.initialFilter} />;
}

// -----------------------------------------------------------------------------
// ShopsPageContent Component
// -----------------------------------------------------------------------------

interface ShopsApiResponse {
  shops: Shop[];
  total: number;
  page: number;
  hasMore: boolean;
}

function ShopsPageContent({ initialFilter }: { initialFilter?: 'favorites' | 'wishlist' }) {
  const [location, setLocation] = useLocation();
  const { isLoggedIn, user, tier, isInsider } = useAuth();
  const { t, locale } = useI18n();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Use interactions context
  const { fetchInteractionsForShops } = useShopInteractions();

  // Use route builder context
  const { shops: routeShops } = useRouteBuilder();

  // Separate input state from actual search query
  const [searchInput, setSearchInput] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [activeCityExact, setActiveCityExact] = useState<{ name: string; province?: string; country?: string } | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);

  const [province, setProvince] = useState<string>("all");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [sort, setSort] = useState<SortOption>("overall");
  const [radius, setRadius] = useState<string>("0");
  const [country, setCountry] = useState<string>(() => {
    return getPreferredGeo(user, locale).country;
  });
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedPriceLevels, setSelectedPriceLevels] = useState<number[]>([]);
  const [selectedParkingRatings, setSelectedParkingRatings] = useState<number[]>([]);
  const [hasCardFilter, setHasCardFilter] = useState(false);
  const [hasCashFilter, setHasCashFilter] = useState(false);
  const todayWeekday = useMemo(() => {
    // Current day in JS: 0=Sun, 1=Mon...
    const jsDay = new Date().getDay();
    // Convert to DB Schema: 0=Mon, 6=Sun
    const dbDay = jsDay === 0 ? 6 : jsDay - 1;
    return String(dbDay);
  }, []);
  const [openOnSelectedDayOnly, setOpenOnSelectedDayOnly] = useState(false);
  const [openOnDayFilter, setOpenOnDayFilter] = useState<string>(todayWeekday);
  const selectedOpeningStatusDay = useMemo(() => {
    const dbDay = Number(openOnDayFilter);
    if (Number.isNaN(dbDay)) return undefined;
    return dbDay; // Now matches schema directly
  }, [openOnDayFilter]);
  const [selectedShopId, setSelectedShopId] = useState<string | null>(null);
  const [highlightedShopId, setHighlightedShopId] = useState<string | null>(null);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [mapZoom, setMapZoom] = useState<number | null>(null);
  const [mapBounds, setMapBounds] = useState<{
    northeast: { lat: number; lng: number };
    southwest: { lat: number; lng: number };
  } | null>(null);
  const [page, setPage] = useState(1);

  // Ambiguity Modal State
  const [regionCandidates, setRegionCandidates] = useState<any[]>([]);
  const [showRegionSelectModal, setShowRegionSelectModal] = useState(false);
  const { prioritizedCountries, remainingCountries } = useMemo(() => {
    const byCode = new Map(EUROPE_COUNTRIES.map((c) => [c.code, c]));
    const prioritized = PRIORITY_COUNTRY_CODES
      .map((code) => byCode.get(code))
      .filter(Boolean) as typeof EUROPE_COUNTRIES;
    const rest = EUROPE_COUNTRIES
      .filter((c) => !PRIORITY_COUNTRY_CODES.includes(c.code))
      .slice()
      .sort((a, b) => a.labelNl.localeCompare(b.labelNl, "nl"));
    return { prioritizedCountries: prioritized, remainingCountries: rest };
  }, []);

  // Fetch provinces for current country
  const { data: dynamicProvinces = [] } = useQuery<any[]>({
    queryKey: ["provinces", country],
    queryFn: async () => {
      const response = await fetch(`/api/regions/provinces?country=${country}`);
      if (!response.ok) return [];
      return response.json();
    }
  });

  // Check if we're on a province-specific or country-specific route
  const provinceRouteKringloop = useRoute("/kringloopwinkels/:province");
  const provinceRouteThrift = useRoute("/thrift-store/:province");
  const provinceRouteSecondHand = useRoute("/second-hand-shop/:province");

  const countryCollectionKringloop = useRoute("/:country/kringloopwinkels");
  const countryCollectionThrift = useRoute("/:country/thrift-stores");
  const countryCollectionSecondHand = useRoute("/:country/second-hand-shops");
  const countryCollectionShops = useRoute("/:country/shops");

  const provinceParams = provinceRouteKringloop[1] || provinceRouteThrift[1] || provinceRouteSecondHand[1];
  const countryParams = countryCollectionKringloop[1] || countryCollectionThrift[1] || countryCollectionSecondHand[1] || countryCollectionShops[1];

  const provinceSlugFromUrl = provinceParams?.province ? decodeURIComponent(provinceParams.province) : null;
  const countrySlugFromUrl = countryParams?.country ? decodeURIComponent(countryParams.country) : null;

  // Shop page is now exclusively for Kringloopwinkels
  const shopType = "kringloop";

  // Page Display SEO/Titles
  const pageTitle = initialFilter === 'favorites' ? t("shops.title.favorites") :
    initialFilter === 'wishlist' ? t("shops.title.wishlist") :
      (activeCityExact?.name) ? t("shops.title.region", { name: activeCityExact.name }) :
        (province && province !== 'all') ? t("shops.title.region", { name: province }) :
          t("shops.title");

  const pageDescription = initialFilter ? t("shops.description.personal") :
    (activeCityExact?.name) ? t("shops.description.region", { name: activeCityExact.name }) :
      (province && province !== 'all') ? t("shops.description.region", { name: province }) :
        t("shops.description");

  // SEO Meta Logic
  const regionNameForSEO = activeCityExact?.name || (province && province !== 'all' ? province : country);
  const isSpecificRegion = !!(activeCityExact?.name || (province && province !== 'all'));

  const regionSEO = getLocalizedRegionSEO({
    regionName: regionNameForSEO as string,
    isCity: !!activeCityExact?.name,
    isProvince: province !== 'all',
    countryCode: country,
    stateOrProvince: province !== 'all' ? province : undefined
  }, locale);

  useSEO({
    titleKey: isSpecificRegion ? "seo.region.title" : "seo.shops.title",
    descriptionKey: "seo.shops.description",
    dynamicValues: { region: regionSEO },
    isRawTitle: true
  });

  const formatCount = (value: number) => new Intl.NumberFormat("nl-NL").format(value);
  const radiusOptions = ["0", "5", "10", "25", "50"];

  // Scroll to top when page changes (e.g. from Home to Shops or switching between Shop types)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location]);

  // Handle province slug from URL
  useEffect(() => {
    if (provinceSlugFromUrl) {
      const fetchRegionBySlug = async () => {
        try {
          // Search for region by slug (which is often just the name)
          const response = await fetch(`/api/regions/search?q=${encodeURIComponent(provinceSlugFromUrl.replace(/-/g, ' '))}&country=${country}`);
          if (response.ok) {
            const data = await response.json();
            const regions = Array.isArray(data) ? data : [data];
            // Find the best match (province and matching slug)
            const region = regions.find((r: any) =>
              (r.type === "province" || r.type === "city") &&
              (slugify(r.name) === provinceSlugFromUrl)
            ) || regions[0];

            if (region) {
              setProvince(region.type === "province" ? region.name : "all");
              if (region.type === "province") setActiveCityExact(null);
              if (region.type === "city") {
                setActiveCityExact({ name: region.name, province: region.province, country: region.country || "NL" });
                setActiveSearch("");
              }
              setCountry(region.country || "NL");

              if (region.bounds) {
                setMapCenter(region.center);
                setMapZoom(region.zoom_level);
                setMapBounds(region.bounds);
              }
            }
          }
        } catch (e) {
          console.error("Failed to detect region from URL slug", e);
        }
      };
      fetchRegionBySlug();
    }
  }, [provinceSlugFromUrl, country]);

  // Handle country slug from URL
  useEffect(() => {
    if (countrySlugFromUrl) {
      const detectedCode = normalizeCountryCode(countrySlugFromUrl);
      if (detectedCode && detectedCode !== country) {
        setCountry(detectedCode);
      }
    }
  }, [countrySlugFromUrl]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [shopType, province, country, activeSearch, activeCityExact, verifiedOnly, radius, selectedCategories, selectedPriceLevels, selectedParkingRatings, hasCardFilter, hasCashFilter, openOnSelectedDayOnly, openOnDayFilter]);

  // Update map zoom and bounds based on radius
  useEffect(() => {
    if (mapCenter && radius && radius !== "0") {
      const radiusKm = parseInt(radius);

      // Calculate bounding box for radius
      const latDegree = 111.32;
      const lngDegree = 40075 * Math.cos(mapCenter.lat * Math.PI / 180) / 360;

      const dLat = radiusKm / latDegree;
      const dLng = radiusKm / lngDegree;

      const newBounds = {
        northeast: { lat: mapCenter.lat + dLat, lng: mapCenter.lng + dLng },
        southwest: { lat: mapCenter.lat - dLat, lng: mapCenter.lng - dLng }
      };

      setMapBounds(newBounds);
      // Let ShopsSearchMap handle the fitting of these bounds
    } else if (mapCenter && activeSearch && radius === "0") {
      // Direct city search without radius extra padding
      const isMobile = window.innerWidth < 768;
      setMapZoom(isMobile ? 12 : 13);
    }
  }, [radius, mapCenter, activeSearch]);

  // Clear highlight after 3 seconds
  useEffect(() => {
    if (highlightedShopId) {
      const timer = setTimeout(() => {
        setHighlightedShopId(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [highlightedShopId]);

  // Scroll to map when a shop is selected (Mobile/Tablet)
  useEffect(() => {
    // User requested scroll to top/map when selecting a shop
    if (selectedShopId && mapRef.current) {
      const mapRect = mapRef.current.getBoundingClientRect();
      const isMapInView = mapRect.top >= 0 && mapRect.bottom <= window.innerHeight;

      // Force scroll on mobile/tablet ONLY
      if (window.innerWidth < 1024) {
        mapRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [selectedShopId]);

  // Handler for search button with region search support
  const handleSearch = async () => {
    const normalize = (value: string) => value.trim().toLowerCase();
    const matchesActiveSearchArea = (region: any) => {
      const selectedCountry = String(country || "").trim().toUpperCase();
      const selectedProvince = String(province || "").trim();
      const regionCountry = String(region?.country || "").trim().toUpperCase();
      const regionProvince = String(region?.province || "").trim();
      const regionName = String(region?.name || "").trim();
      const regionType = String(region?.type || "").toLowerCase();

      if (selectedCountry && selectedCountry !== "ALL" && regionCountry && regionCountry !== selectedCountry) {
        return false;
      }

      if (selectedProvince && selectedProvince !== "all") {
        if (regionType === "province") {
          return normalize(regionName) === normalize(selectedProvince);
        }
        return normalize(regionProvince) === normalize(selectedProvince);
      }

      return true;
    };

    // Try region search first (province/city)
    if (searchInput.trim().length > 0) {
      try {
        const response = await fetch(`/api/regions/search?q=${encodeURIComponent(searchInput)}&country=${country}`);
        if (response.ok) {
          const regionData = await response.json();

          // Handle array response (new format)
          if (Array.isArray(regionData) && regionData.length > 0) {
            // Separate real database regions from geocoded fallback results
            const dbRegions = regionData.filter((r: any) => !String(r.id || "").startsWith("geocoded-"));
            const geocodedRegions = regionData.filter((r: any) => String(r.id || "").startsWith("geocoded-"));

            // Prefer database regions; for geocoded fallbacks, also run shop name search
            const constrainedRegions = dbRegions.length > 0
              ? dbRegions.filter(matchesActiveSearchArea)
              : geocodedRegions.filter(matchesActiveSearchArea);
            const isGeocodedFallback = dbRegions.length === 0 && geocodedRegions.length > 0;

            if (constrainedRegions.length === 0) {
              // No region match inside selected search area; fall back to text search below.
            } else {
              // Ambiguity check: if multiple results, show modal
              if (constrainedRegions.length > 1) {
                setRegionCandidates(constrainedRegions);
                setShowRegionSelectModal(true);
                return;
              }

              // Single result logic
              const region = constrainedRegions[0];
              console.log(`[ShopsPage] Region found: ${region.name}${isGeocodedFallback ? " (geocoded)" : ""}`);

              setMapCenter(region.center);
              setMapZoom(region.zoom_level);
              setMapBounds(region.bounds);
              if (country === "all" && region.country) {
                setCountry(region.country || "NL");
              }

              if (region.type === "province") {
                setProvince(region.name);
                setActiveSearch(isGeocodedFallback ? searchInput.trim() : "");
                setActiveCityExact(null);
                setSearchInput(region.name);
              } else if (region.type === "city") {
                if (province === "all" && region.province) setProvince(region.province);
                // For geocoded fallback results (small villages not in our DB), also keep
                // the shop name search active so shops matching the search term show up.
                // IMPORTANT: For fallback results (like "Emmaus Paris"), we must NOT set activeCityExact
                // otherwise the backend will filter for shops with city="Emmaus Paris" which yields 0 results.
                setActiveSearch(isGeocodedFallback ? searchInput.trim() : "");
                setActiveCityExact(isGeocodedFallback ? null : { name: region.name, province: region.province, country: region.country || "NL" });
                setSearchInput(region.name);
              }
              return;
            }
          }

          // Backward compatibility
          if (regionData && !Array.isArray(regionData) && regionData.name) {
            const region = regionData;
            setMapCenter(region.center);
            setMapZoom(region.zoom_level);
            setMapBounds(region.bounds);
            setCountry(region.country || "NL");
            if (region.type === "province") {
              setProvince(region.name);
              setActiveSearch("");
              setActiveCityExact(null);
            } else if (region.type === "city") {
              if (region.province) setProvince(region.province);
              setActiveSearch("");
              setActiveCityExact({ name: region.name, province: region.province, country: region.country || "NL" });
            }
            return;
          }
        }
      } catch (error) {
        console.log("[ShopsPage] No region found, falling back to shop search");
      }
    }

    // Fallback to normal shop search
    const alternate = getAlternatePlaceName(searchInput.trim());
    const term = alternate ? `${searchInput.trim()} ${alternate}` : searchInput.trim();

    // Keep current country/province filters to enforce selected search area.
    setActiveCityExact(null);

    setActiveSearch(term);

    // Always clear map constraints for a text search to let the results dictate the view
    // (unless user specifically locked a radius, but here we reset radius anyway for fresh search)
    setMapCenter(null);
    setMapZoom(null);
    setMapBounds(null);
  };

  const handleRegionSelect = (region: any) => {
    setMapCenter(region.center);
    setMapZoom(region.zoom_level);
    setMapBounds(region.bounds);

    if (region.type === "province") {
      setProvince(region.name);
      if (country === "all" && region.country) {
        setCountry(region.country || "NL");
      }
      setActiveSearch("");
      setActiveCityExact(null);
      setSearchInput(region.name);
    } else if (region.type === "city") {
      if (province === "all" && region.province) setProvince(region.province);
      if (country === "all" && region.country) {
        setCountry(region.country || "NL");
      }
      setActiveSearch("");
      setActiveCityExact({ name: region.name, province: region.province, country: region.country || "NL" });
      setSearchInput(region.name);
    }
    setShowRegionSelectModal(false);
  };

  // Handler for Enter key in search input
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Fetch shops with standard Pagination
  const {
    data,
    isLoading,
    error,
    isFetching
  } = useQuery<ShopsApiResponse>({
    queryKey: ["shops", shopType, province, country, activeSearch, activeCityExact, verifiedOnly, radius, page, selectedCategories, selectedPriceLevels, selectedParkingRatings, hasCardFilter, hasCashFilter, openOnSelectedDayOnly, openOnDayFilter, initialFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("page", page.toString());
      params.set("limit", "20"); // Load 20 shops per page for better performance
      params.set("shopType", shopType);

      if (province && province !== "all") {
        params.set("province", province);
      }

      // Add filter for favorites/wishlist
      if (initialFilter) {
        params.set(initialFilter, "true");
      }

      // Get auth token for protected routes
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const headers: HeadersInit = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;

      if (country) {
        params.set("country", country);
      }
      params.set("locale", locale);
      if (activeCityExact?.name) {
        params.set("cityExact", activeCityExact.name);
        if (activeCityExact.province) params.set("cityProvince", activeCityExact.province);
      }

      if (selectedCategories.length > 0) {
        params.set("categories", selectedCategories.join(","));
      }

      if (selectedPriceLevels.length > 0) {
        params.set("priceLevels", selectedPriceLevels.join(","));
      }

      if (selectedParkingRatings.length > 0) {
        params.set("parkingRatings", selectedParkingRatings.join(","));
      }

      if (hasCardFilter) params.set("hasCard", "true");
      if (hasCashFilter) params.set("hasCash", "true");
      if (openOnSelectedDayOnly) params.set("openOnDay", openOnDayFilter);

      // Keep text search active regardless of selected radius.
      if (activeSearch) {
        params.set("search", activeSearch);
      }

      if (verifiedOnly) {
        params.set("status", "verified");
      }

      // Add radius and location if available
      if (mapCenter) {
        params.set("lat", mapCenter.lat.toString());
        params.set("lng", mapCenter.lng.toString());
      }

      if (radius && radius !== "0") {
        params.set("radius", radius);
        // If radius is active, prioritize distance sort unless other sort matches
        if (sort === "overall" || sort === "distance") {
          params.set("sort", "distance");
        } else {
          params.set("sort", sort);
        }

        // CRITICAL FIX: If radius is active, we MUST ignore all strict location filters
        // to allow finding shops in a radius around a point, even if that point
        // itself (like a small village) has no shops in the database.
        params.delete("cityExact");
        params.delete("cityProvince");
        params.delete("province");

        // SMART FIX: If a radius is active, the city name in the search term 
        // is redundant as a location filter and prevents finding nearby shops 
        // in other towns. We remove the city name from the search term.
        if (activeSearch && activeCityExact?.name) {
          const cityName = activeCityExact.name.toLowerCase().trim();
          const cleanSearch = activeSearch.toLowerCase()
            .replace(cityName, "")
            .trim();

          if (cleanSearch) {
            params.set("search", cleanSearch);
          } else {
            params.delete("search");
          }
        } else if (activeSearch) {
          params.set("search", activeSearch);
        }
      } else {
        params.set("sort", sort);
        if (activeSearch) {
          params.set("search", activeSearch);
        }
      }

      // Expensive total count is only needed on first page.
      params.set("includeTotal", page === 1 ? "true" : "false");

      const response = await fetch(`/api/shops?${params.toString()}`, { headers });

      let result: ShopsApiResponse = {
        shops: [], total: 0, page: page, hasMore: false
      };

      if (!response.ok) {
        const errorText = await response.text();
        console.error("[ShopsPage] API error:", response.status, errorText);
        // On error, return empty result to prevent crash
        return result;
      } else {
        result = await response.json();

        // CACHE LOGIC: Save shops to OfflineCache (72h)
        // Only cache the first page of default results to keep size manageable
        // and ensure the map has *something* to show offline.
        if (page === 1 && !activeSearch && province === 'all') {
          OfflineCache.set('shops-list-default', result.shops, 72);
        }
      }

      const shopsData = result.shops || [];

      if (import.meta.env.DEV) {
        console.log(`[ShopsPage] Fetched page ${page}, items: ${shopsData.length}`);
      }

      // STAP 2: Valideer en sanitize lat/lng voor shops die coördinaten hebben
      const sanitizedShops = shopsData.map(shop => {
        // EERST controleren of shop überhaupt coördinaten heeft
        if (shop.lat == null || shop.lng == null) {
          return { ...shop, lat: null, lng: null };
        }

        let lat: number | null = null;
        let lng: number | null = null;

        const latNum = typeof shop.lat === "string" ? parseFloat(shop.lat) : shop.lat;
        const lngNum = typeof shop.lng === "string" ? parseFloat(shop.lng) : shop.lng;

        if (typeof latNum === "number" && !isNaN(latNum)) {
          lat = latNum;
        }
        if (typeof lngNum === "number" && !isNaN(lngNum)) {
          lng = lngNum;
        }

        return { ...shop, lat, lng };
      });

      return {
        ...result,
        shops: sanitizedShops
      };
    },
    // use placeholderData to keep previous data while fetching new data
    // this prevents the "isLoading" state from flashing and unmounting the map
    placeholderData: (previousData) => {
      if (previousData) return previousData;
      // Fallback to offline cache for initial load
      const cached = OfflineCache.get('shops-list-default');
      if (cached && page === 1 && !activeSearch) {
        return { shops: cached, total: cached.length, page: 1, hasMore: false };
      }
      return undefined;
    },
  });

  // Always fetch global shop count (unfiltered) for hero copy under "Kringloopwinkels"
  const { data: globalShopCount = 0 } = useQuery<number>({
    queryKey: ["shops-global-count"],
    queryFn: async () => {
      const response = await fetch("/api/metrics/public-counts");
      if (!response.ok) return 0;
      const result = await response.json();
      return Number(result?.activeShopsGlobal || 0);
    },
    staleTime: 1000 * 60 * 5,
  });

  // Get list of shops from current page
  const allShops = useMemo(() => {
    return data?.shops || [];
  }, [data]);

  const totalShops = data?.total || 0;
  const hasMore = data?.hasMore || false;

  useEffect(() => {
    if (country === "all") return;
    if (isFetching) return;
    if (totalShops > 0) return;
    // If selected/default country has no results, gracefully fallback.
    setCountry("all");
  }, [country, isFetching, totalShops]);

  // BULK FETCH INTERACTIONS
  // Trigger fetch when new shops are loaded or login state changes
  useEffect(() => {
    if (allShops.length && isLoggedIn) {
      const shopIds = allShops.map(s => s.id);
      fetchInteractionsForShops(shopIds);
    }
  }, [allShops, isLoggedIn, fetchInteractionsForShops]);

  // Sort shops client-side
  // NOTE: For most cases we trust the server's order (which sorts the ENTIRE database).
  // Client-side sorting here only sorts the CURRENT PAGE, which can be confusing.
  const sortedShops = useMemo(() => {
    // If we're using "Beste" (overall) or "Google", trust the server's global sort.
    if (sort === "overall" || sort === "google" || sort === "vintmap" || sort === "distance") {
      return allShops;
    }

    const sorted = [...allShops];
    if (sort === "eigen") {
      sorted.sort((a, b) => ((b as any).ownerRating || 0) - ((a as any).ownerRating || 0));
    } else if (sort === "popular") {
      // Sort by likes + saves count (mirrors server logic)
      sorted.sort((a, b) => {
        const scoreA = (a.likesCount || 0) + (a.savesCount || 0);
        const scoreB = (b.likesCount || 0) + (b.savesCount || 0);
        return scoreB - scoreA;
      });
    }
    return sorted;
  }, [allShops, sort]);

  // Auto-zoom map when province changes or search is executed
  useEffect(() => {
    async function updateMapCenter() {
      if (!isLoggedIn) {
        setMapCenter(null);
        setMapZoom(null);
        setMapBounds(null);
        return;
      }

      if (province && province !== "all") {
        // Province selected - zoom to province
        // Note: If mapBounds was set by handleSearch/handleRegionSelect, 
        // it will be passed to the map and handled there.
        if (!mapBounds) {
          const provinceCenter = getProvinceCenter(province);
          if (provinceCenter) {
            setMapCenter(provinceCenter);
            setMapZoom(provinceCenter.zoom);
          }
        }
      } else if (activeSearch) {
        // If mapBounds is already set from handleSearch, don't overwrite it
        if (!mapBounds) {
          // Search executed (could be city name) - try to geocode
          const cityCenter = await getCityCenter(activeSearch, province !== "all" ? province : undefined, country);
          if (cityCenter) {
            setMapCenter(cityCenter);
            setMapZoom(13);
          } else {
            // Fallback to shops
            setMapCenter(null);
            setMapZoom(null);
            // No bounds for geocoding result usually
          }
        }
      } else {
        // No filter - reset
        setMapCenter(null);
        setMapZoom(null);
        setMapBounds(null);
      }
    }
    updateMapCenter();
  }, [province, activeSearch, isLoggedIn]); // We intentionaly DON'T depend on mapBounds here to avoid loop

  // Move selected shop to the top of the list ONLY on mobile/tablet
  const filteredShops = useMemo(() => {
    const shops = sortedShops || [];
    const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;

    if (!selectedShopId || isDesktop) return shops;

    const selectedShop = shops.find(s => s.id === selectedShopId);
    if (!selectedShop) return shops;

    return [
      selectedShop,
      ...shops.filter(s => s.id !== selectedShopId)
    ];
  }, [sortedShops, selectedShopId]);

  // Stabilize the shop select handler to prevent map re-renders
  const handleShopSelect = useCallback((shopId: string) => {
    setSelectedShopId(shopId);
    const element = document.getElementById(`shop-item-${shopId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedShopId(shopId);
    }
  }, []);

  const handleAddToRoute = () => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <FeatureGuard feature="shops" cacheKey="shops-list-default" ttlHours={72}>
        <main className="flex-1 container mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold">{pageTitle}</h1>
            <p className="text-muted-foreground">
              {pageDescription}
            </p>
            {!initialFilter && (
              <p className="text-muted-foreground">
                {t("shops.roadtrip", { count: formatCount(globalShopCount) })}
              </p>
            )}
          </div>

          <div className="space-y-2 mb-6">
            {/* Search + radius/day/open controls */}
            <div className="w-full flex flex-col lg:flex-row gap-2">
              <div className="w-full flex gap-2">
                <Input
                  placeholder={t("shops.search.placeholder")}
                  value={searchInput}
                  onChange={(e) => {
                    setSearchInput(e.target.value);
                    if (activeCityExact) setActiveCityExact(null);
                  }}
                  onKeyDown={handleSearchKeyPress}
                  className="flex-1"
                  data-testid="input-search-shops"
                />
                <Button
                  type="button"
                  onClick={handleSearch}
                  variant="default"
                  className="px-4"
                  data-testid="button-search"
                >
                  <Search className="h-4 w-4 mr-2" />
                  {t("shops.search.button")}
                </Button>
              </div>

              <div className="w-full md:w-auto flex flex-col sm:flex-row gap-2">
                <Select value={radius} onValueChange={setRadius}>
                  <SelectTrigger className="w-full sm:w-[110px]" data-testid="select-radius-search">
                    <SelectValue placeholder={t("shops.filters.radius")} />
                  </SelectTrigger>
                  <SelectContent>
                    {radiusOptions.map((radiusValue) => (
                      <SelectItem key={radiusValue} value={radiusValue}>
                        {radiusValue} km
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={openOnDayFilter} onValueChange={setOpenOnDayFilter}>
                  <SelectTrigger className="w-full sm:w-[150px]" data-testid="select-open-day">
                    <Clock className="h-3.5 w-3.5 mr-2" />
                    <SelectValue placeholder={t("shops.filters.day")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">{t("shops.filters.day.monday")}</SelectItem>
                    <SelectItem value="1">{t("shops.filters.day.tuesday")}</SelectItem>
                    <SelectItem value="2">{t("shops.filters.day.wednesday")}</SelectItem>
                    <SelectItem value="3">{t("shops.filters.day.thursday")}</SelectItem>
                    <SelectItem value="4">{t("shops.filters.day.friday")}</SelectItem>
                    <SelectItem value="5">{t("shops.filters.day.saturday")}</SelectItem>
                    <SelectItem value="6">{t("shops.filters.day.sunday")}</SelectItem>
                  </SelectContent>
                </Select>

                <div className="h-10 px-3 rounded-md border bg-background flex items-center gap-2">
                  <Checkbox
                    id="filter-open-selected-day"
                    checked={openOnSelectedDayOnly}
                    onCheckedChange={(val) => setOpenOnSelectedDayOnly(!!val)}
                    data-testid="checkbox-open-selected-day"
                  />
                  <Label htmlFor="filter-open-selected-day" className="cursor-pointer text-sm whitespace-nowrap">
                    {t("shops.filters.openOnSelectedDay")}
                  </Label>
                </div>
              </div>
            </div>

            {/* Filters Row 2: location + advanced filters */}
            <div className="flex flex-wrap gap-2">
              <Select value={country} onValueChange={(val) => {
                setCountry(val);
                setProvince("all");
                setActiveCityExact(null);
                setPage(1);
                // Store user's preference for future visits
                if (val !== "all") {
                  setUserCountry(val);
                }
              }}>
                <SelectTrigger className="w-full sm:w-[140px] flex-1 min-w-0 sm:min-w-[120px]" data-testid="select-country">
                  <MapPinIcon className="h-4 w-4 mr-2" />
                  <SelectValue placeholder={t("shops.country.placeholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("shops.country.all")}</SelectItem>
                  {prioritizedCountries.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.labelNl}</SelectItem>
                  ))}
                  {remainingCountries.length > 0 && <SelectSeparator />}
                  {remainingCountries.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.labelNl}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={province}
                onValueChange={async (val) => {
                  setProvince(val);
                  setPage(1);

                  // Clear search when switching province
                  setSearchInput("");
                  setActiveSearch("");
                  setActiveCityExact(null);
                  setRadius("0");

                  if (val !== "all") {
                    try {
                      const response = await fetch(`/api/regions/search?q=${encodeURIComponent(val)}&country=${country}`);
                      if (response.ok) {
                        const data = await response.json();
                        const region = Array.isArray(data) ? data[0] : data;
                        if (region && region.bounds) {
                          setMapCenter(region.center);
                          setMapZoom(region.zoom_level);
                          setMapBounds(region.bounds);
                        }
                      }
                    } catch (e) {
                      console.error("Failed to fetch province bounds", e);
                    }
                  } else {
                    setMapBounds(null);
                    setMapCenter(null);
                    setMapZoom(null);
                  }
                }}
              >
                <SelectTrigger className="w-full sm:w-[180px] flex-1 min-w-0 sm:min-w-[140px]" data-testid="select-province">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder={t("shops.province.placeholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("shops.province.all")}</SelectItem>
                  {dynamicProvinces.map(p => (
                    <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full sm:w-auto flex-1 min-w-0 sm:min-w-[140px]">
                    <Filter className="h-4 w-4 mr-2" />
                    {t("shops.filters.more")}
                    {(selectedCategories.length > 0 || selectedPriceLevels.length > 0 || selectedParkingRatings.length > 0 || hasCardFilter || hasCashFilter || openOnSelectedDayOnly) && (
                      <Badge variant="secondary" className="ml-2 px-1 h-5 min-w-5 flex items-center justify-center">
                        {[
                          selectedCategories.length > 0,
                          selectedPriceLevels.length > 0,
                          selectedParkingRatings.length > 0,
                          hasCardFilter,
                          hasCashFilter,
                          openOnSelectedDayOnly
                        ].filter(Boolean).length}
                      </Badge>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[calc(100vw-2rem)] max-w-[300px] p-4 space-y-4 max-h-[80vh] overflow-y-auto" align="end">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <h4 className="font-medium leading-none">{t("shops.filters.shopOptions")}</h4>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-medium leading-none">{t("shops.filters.payment")}</h4>
                      <div className="flex items-center space-x-2">
                        <Checkbox id="filter-card" checked={hasCardFilter} onCheckedChange={(val) => setHasCardFilter(!!val)} />
                        <Label htmlFor="filter-card" className="flex items-center cursor-pointer">
                          <CreditCard className="h-4 w-4 mr-2" />
                          {t("shops.filters.card")}
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox id="filter-cash" checked={hasCashFilter} onCheckedChange={(val) => setHasCashFilter(!!val)} />
                        <Label htmlFor="filter-cash" className="flex items-center cursor-pointer">
                          <Banknote className="h-4 w-4 mr-2" />
                          {t("shops.filters.cashOnly")}
                        </Label>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-medium leading-none">{t("shops.filters.priceLevel")}</h4>
                      <div className="flex flex-col gap-2 mt-2">
                        {[1, 2, 3, 4, 5, 0].map(level => {
                          const isSelected = selectedPriceLevels.includes(level);
                          return (
                            <button
                              key={level}
                              type="button"
                              onClick={() => {
                                if (isSelected) {
                                  setSelectedPriceLevels(selectedPriceLevels.filter(l => l !== level));
                                } else {
                                  setSelectedPriceLevels([...selectedPriceLevels, level]);
                                }
                              }}
                              className={cn(
                                "h-10 px-3 rounded-md border flex items-center justify-between transition-all w-full",
                                isSelected
                                  ? "bg-primary/10 border-primary text-primary"
                                  : "bg-background hover:bg-muted border-input text-muted-foreground"
                              )}
                            >
                              {level > 0 ? (
                                <PriceLevel
                                  level={level}
                                  country={country !== "all" ? country : detectUserCountry()}
                                  size="sm"
                                />
                              ) : (
                                <span className="text-sm">{t("shops.filters.notKnown")}</span>
                              )}
                              {isSelected && <CheckCircle className="h-4 w-4 ml-2" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-medium leading-none">{t("shops.filters.parking")}</h4>
                      <div className="flex flex-col gap-2 mt-2">
                        {[1, 2, 3, 0].map(level => {
                          const isSelected = selectedParkingRatings.includes(level);
                          return (
                            <button
                              key={level}
                              type="button"
                              onClick={() => {
                                if (isSelected) {
                                  setSelectedParkingRatings(selectedParkingRatings.filter(l => l !== level));
                                } else {
                                  setSelectedParkingRatings([...selectedParkingRatings, level]);
                                }
                              }}
                              className={cn(
                                "h-10 px-3 rounded-md border flex items-center justify-between transition-all w-full",
                                isSelected
                                  ? "bg-primary/10 border-primary text-primary"
                                  : "bg-background hover:bg-muted border-input text-muted-foreground"
                              )}
                            >
                              <div className="flex items-center gap-1">
                                {level > 0 ? (
                                  <>
                                    {Array.from({ length: level }).map((_, i) => (
                                      <Car key={i} className={cn("h-4 w-4", isSelected ? "fill-primary" : "text-muted-foreground/50")} />
                                    ))}
                                    <span className="ml-2 text-xs">
                                      {level === 1 ? "Slecht" : level === 2 ? "Oké" : "Makkelijk"}
                                    </span>
                                  </>
                                ) : (
                                  <span className="text-sm">{t("shops.filters.notKnown")}</span>
                                )}
                              </div>
                              {isSelected && <CheckCircle className="h-4 w-4 ml-2" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-medium leading-none">{t("shops.filters.categories")}</h4>
                      <div className="grid grid-cols-1 gap-2 mt-2">
                        {CATEGORIES.map(cat => (
                          <div key={cat} className="flex items-center space-x-2">
                            <Checkbox
                              id={`cat-${cat}`}
                              checked={selectedCategories.includes(cat)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedCategories([...selectedCategories, cat]);
                                } else {
                                  setSelectedCategories(selectedCategories.filter(c => c !== cat));
                                }
                              }}
                            />
                            <Label htmlFor={`cat-${cat}`} className="text-sm cursor-pointer">{cat}</Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full mt-2 text-xs h-8"
                    onClick={() => {
                      setSelectedCategories([]);
                      setSelectedPriceLevels([]);
                      setSelectedParkingRatings([]);
                      setHasCardFilter(false);
                      setHasCashFilter(false);
                      setOpenOnSelectedDayOnly(false);
                      setOpenOnDayFilter(todayWeekday);
                    }}
                  >
                    {t("shops.filters.clear")}
                  </Button>
                </PopoverContent>
              </Popover>
              <Select value={sort} onValueChange={(v) => setSort(v as SortOption)}>
                <SelectTrigger className="w-full sm:w-[150px] flex-1 min-w-0 sm:min-w-[140px]" data-testid="select-sort">
                  <SortAsc className="h-4 w-4 mr-2" />
                  <SelectValue placeholder={t("shops.sort.placeholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="overall">{t("shops.sort.overall")}</SelectItem>
                  <SelectItem value="vintmap">{t("shops.sort.vintmap")}</SelectItem>
                  <SelectItem value="google">{t("shops.sort.google")}</SelectItem>
                  <SelectItem value="popular">{t("shops.sort.popular")}</SelectItem>
                  <SelectItem value="newest">{t("shops.sort.newest")}</SelectItem>
                  <SelectItem value="name-asc">{t("shops.sort.nameAsc")}</SelectItem>
                  <SelectItem value="distance">{t("shops.sort.distance")}</SelectItem>
                </SelectContent>
              </Select>

              <Button
                onClick={() => {
                  if (routeShops.length === 0) return;

                  if (!isLoggedIn) {
                    setShowLoginModal(true);
                    return;
                  }

                  if (!isInsider) {
                    setShowUpgradeModal(true);
                    return;
                  }
                  // Navigate to route builder page
                  setLocation('/routes/new');
                }}
                disabled={routeShops.length === 0}
                variant="outline"
                className={`flex-1 sm:flex-none sm:h-11 sm:px-4 gap-2 font-medium sm:text-base ${routeShops.length > 0
                  ? 'bg-primary/10 hover:bg-primary/20 border-primary text-primary'
                  : 'text-muted-foreground opacity-50 cursor-not-allowed'
                  }`}
              >
                <MapPinIcon className="h-4 w-4" />
                <span className="hidden sm:inline">
                  Route {routeShops.length > 0 ? `(${routeShops.length})` : ''}
                </span>
                <span className="sm:hidden">
                  Route {routeShops.length > 0 ? `(${routeShops.length})` : ''}
                </span>
              </Button>


              <Button
                onClick={() => setLocation("/kringloopwinkels/voeg-toe")}
                variant="outline"
                className="flex-1 sm:flex-none gap-2 bg-primary/5 hover:bg-primary/10 border-primary/20 text-primary font-medium"
              >
                <PlusCircle className="h-4 w-4" />
                <span className="hidden sm:inline">{t("shops.addShop")}</span>
                <span className="sm:hidden">{t("shops.add")}</span>
              </Button>
            </div>
          </div>

          {/* Results Count */}
          {
            (totalShops > 0 || (mapCenter && activeSearch) || province !== "all") && (
              <div className="mb-4 text-sm text-muted-foreground font-medium flex items-center flex-wrap gap-2">
                {mapCenter && activeSearch ? (
                  <div className="flex items-center gap-2">
                    <span>
                      {t("shops.results.withinRadius", {
                        count: totalShops,
                        plural: totalShops === 1 ? t("shops.common.shopSingular") : t("shops.common.shopPlural"),
                        radius,
                        search: activeSearch
                      })}
                    </span>
                  </div>
                ) : province !== "all" ? (
                  <span>
                    {t("shops.results.inProvince", {
                      count: totalShops,
                      plural: totalShops === 1 ? t("shops.common.shopSingular") : t("shops.common.shopPlural"),
                      province
                    })}
                  </span>
                ) : (
                  <span>{t("shops.results.showing", { shown: filteredShops.length, total: totalShops })}</span>
                )}

                {isFetching && <Loader2 className="h-3 w-3 animate-spin ml-2" />}
              </div>
            )
          }

          {/* Main Grid: List left, Map right. Map is ALWAYS rendered to prevent flickering */}
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-6 relative">

            {/* List Column: Handles Loading/Error/Empty states internally */}
            <div className="order-2 lg:order-1 space-y-6">
              {isLoading && !data ? (
                // Only show full loader if we have NO data (initial load)
                <div className="text-center py-16">
                  <Loader2 className="h-12 w-12 mx-auto text-primary animate-spin mb-4" />
                  <p className="text-muted-foreground">{t("shops.results.loading")}</p>
                </div>
              ) : error ? (
                <div className="text-center py-16">
                  <Store className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold text-lg mb-2">{t("shops.results.errorTitle")}</h3>
                  <p className="text-muted-foreground mb-4">
                    {t("shops.results.errorDesc")}
                  </p>
                  <Button variant="outline" onClick={() => window.location.reload()}>
                    {t("shops.results.retry")}
                  </Button>
                </div>
              ) : filteredShops.length === 0 ? (
                <div className="text-center py-16 px-4">
                  <Store className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold text-xl mb-2">{t("shops.empty.title")}</h3>
                  <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                    {t("shops.empty.desc")}
                  </p>

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                    {isLoggedIn ? (
                      <Link href="/kringloopwinkels/voeg-toe">
                        <Button variant="default" className="w-full sm:w-auto">
                          <Store className="h-4 w-4 mr-2" />
                          {t("shops.empty.add")}
                        </Button>
                      </Link>
                    ) : (
                      <Link href="/auth">
                        <Button variant="outline" className="w-full sm:w-auto">
                          <PlusCircle className="h-4 w-4 mr-2" />
                          {t("shops.empty.loginToAdd")}
                        </Button>
                      </Link>
                    )}

                    {(activeSearch || province !== "all" || verifiedOnly || openOnSelectedDayOnly) && (
                      <Button variant="outline" className="w-full sm:w-auto" onClick={() => {
                        setSearchInput("");
                        setActiveSearch("");
                        setProvince("all");
                        setVerifiedOnly(false);
                        setRadius("0");
                        setOpenOnSelectedDayOnly(false);
                        setOpenOnDayFilter(todayWeekday);
                      }}>
                        {t("shops.filters.clear")}
                      </Button>
                    )}
                  </div>

                  {mapCenter && activeSearch && (
                    <div className="space-y-4">
                      <p className="text-sm font-medium text-muted-foreground">{t("shops.empty.expandSearch")}</p>
                      <div className="flex flex-wrap justify-center gap-2">
                        {["5", "10", "25", "50"].map((r) => (
                          <Button
                            key={r}
                            variant="secondary"
                            size="sm"
                            onClick={() => setRadius(r)}
                            className="rounded-full"
                          >
                            + {r} km
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // RESULT LIST
                <>
                  <div className="space-y-4">
                    {(filteredShops || []).map(shop => (
                      <div
                        key={shop.id}
                        id={`shop-item-${shop.id}`}
                        onClick={() => handleShopSelect(shop.id)}
                        className={`cursor-pointer transition-all duration-500 rounded-xl ${selectedShopId === shop.id || highlightedShopId === shop.id
                          ? "shadow-lg scale-[1.02]"
                          : "hover:shadow-md"
                          } ${highlightedShopId === shop.id ? 'bg-primary/10' : ''}`}
                      >
                        <ShopCard
                          shop={shop}
                          onAddToRoute={handleAddToRoute}
                          isSelected={selectedShopId === shop.id || highlightedShopId === shop.id}
                          openingStatusDay={selectedOpeningStatusDay}
                        />
                      </div>
                    ))}
                  </div>

                  {/* Pagination Controls */}
                  {(page > 1 || hasMore) && (
                    <div className="flex justify-center items-center gap-4 pt-4 pb-8">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setPage(p => Math.max(1, p - 1));
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        disabled={page === 1 || isFetching}
                      >
                        {t("shops.pagination.prev")}
                      </Button>

                      <span className="text-sm font-medium">
                        {t("shops.pagination.page")} {page}
                      </span>

                      <Button
                        variant="outline"
                        onClick={() => {
                          setPage(p => p + 1);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        disabled={!hasMore || isFetching}
                      >
                        {t("shops.pagination.next")}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Map Column: Always Visible */}
            <div ref={mapRef} className="order-1 lg:order-2 lg:sticky lg:top-4 h-[400px] lg:h-[calc(100vh-8rem)] scroll-mt-20">
              <ShopsSearchMapOsm
                shops={filteredShops}
                selectedShopId={selectedShopId || highlightedShopId}
                onShopSelect={handleShopSelect}
                height="100%"
                className="rounded-lg border"
                center={mapCenter}
                zoom={mapZoom}
                bounds={mapBounds}
              />
            </div>
          </div>

        </main>
      </FeatureGuard>
      <LoginModal
        open={showLoginModal}
        onOpenChange={setShowLoginModal}
        action="route"
      />

      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        variant="route-builder"
      />

      <RegionSelectModal
        isOpen={showRegionSelectModal}
        onClose={() => setShowRegionSelectModal(false)}
        candidates={regionCandidates}
        onSelect={handleRegionSelect}
      />


    </div >
  );
}

// -----------------------------------------------------------------------------
// Helper Component: Region Select Modal
// -----------------------------------------------------------------------------
function RegionSelectModal({
  isOpen,
  onClose,
  candidates,
  onSelect
}: {
  isOpen: boolean;
  onClose: () => void;
  candidates: any[];
  onSelect: (region: any) => void;
}) {
  const { t } = useI18n();
  if (!isOpen) return null;

  const typeLabel = (regionType?: string) => {
    const regionTypeNormalized = String(regionType || "").toLowerCase();
    if (regionTypeNormalized === "province") return t("shops.region.province");
    if (regionTypeNormalized === "county") return t("shops.region.county");
    if (regionTypeNormalized === "municipality") return t("shops.region.municipality");
    return t("shops.region.city");
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-lg">
        <h3 className="text-lg font-bold mb-4">{t("shops.region.whichOne")}</h3>
        <p className="text-gray-600 mb-4">
          {t("shops.region.multipleFound")}
        </p>
        <div className="space-y-2">
          {candidates.map((region) => (
            <button
              key={region.id}
              onClick={() => onSelect(region)}
              className="w-full text-left p-3 border rounded hover:bg-gray-100 flex items-center justify-between transition-colors group"
            >
              <span>
                <div className="font-bold text-gray-900">{region.name}</div>
                <div className="text-xs text-gray-500 uppercase tracking-wider font-semibold">
                  {typeLabel(region.type)}{region.province ? ` • ${region.province}` : ""} • {region.country || "NL"}
                </div>
              </span>
              <span className="text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">{t("shops.region.choose")} &rarr;</span>
            </button>
          ))}
        </div>
        <button
          onClick={onClose}
          className="mt-4 w-full p-2 text-gray-500 hover:text-gray-700 text-sm"
        >
          {t("shops.cancel")}
        </button>
      </div>
    </div>
  );
}

